import { useEffect, useState } from 'react';
import { CreateGameDialog } from '../components/CreateGameDialog';
import { CrosswordBanner } from '../components/CrosswordBanner';
import { apiJson } from '../api';
import type { ClientSession, GlobalRankEntry, SettingsState } from '../types';

interface HomeProps {
  settings: SettingsState;
  onSettingsChange: (settings: SettingsState) => void;
  onSession: (session: ClientSession) => void;
}

function formatRankMetric(entry: GlobalRankEntry): string {
  if (entry.points > 0) return `${entry.points} pts`;
  if (entry.fastest_time === null) return 'No score';
  return formatTime(entry.fastest_time);
}

function formatTime(secondsValue: number): string {
  const minutes = Math.floor(secondsValue / 60);
  const seconds = secondsValue - minutes * 60;
  return `${minutes}:${seconds.toFixed(3).padStart(6, '0')}`;
}

interface RecentLeaderboards {
  points_leaderboard: GlobalRankEntry[];
  time_leaderboard: GlobalRankEntry[];
}

export function Home({ settings, onSettingsChange, onSession }: HomeProps) {
  const [joinGameId, setJoinGameId] = useState(() => new URLSearchParams(location.search).get('game') ?? '');
  const [error, setError] = useState('');
  const [createError, setCreateError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [ranking, setRanking] = useState<RecentLeaderboards | null>(null);

  useEffect(() => {
    apiJson<RecentLeaderboards>('/api/leaderboards/recent')
      .then((body) => setRanking(body))
      .catch(() => setRanking({ points_leaderboard: [], time_leaderboard: [] }));
  }, []);

  const createGame = async () => {
    setBusy(true);
    setCreateError('');
    try {
      const body = await apiJson<ClientSession & { join_url: string }>('/api/games/create', {
          method: 'POST',
          body: JSON.stringify({
            mode: settings.mode,
            play_type: settings.playType,
            answer_check_mode: settings.answerCheckMode,
            turn_limit_seconds: settings.playType === 'turn-based' ? settings.turnLimit : null,
            player_name: settings.playerName,
          }),
        });
      onSession(body);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Unable to create game');
    } finally {
      setBusy(false);
    }
  };

  const joinGame = async () => {
    const gameId = joinGameId.trim();
    if (!gameId) return;
    setBusy(true);
    setError('');
    try {
      const body = await apiJson<ClientSession>(`/api/games/${encodeURIComponent(gameId)}/join`, {
          method: 'POST',
          body: JSON.stringify({ player_name: settings.playerName }),
        });
      // Use the server's resolved game_id, not the raw input: typing just the
      // short join code (e.g. "88EDUR") is valid, but the canonical id also
      // has the year/week prefix (e.g. "2026-W38-88edur") -- every later
      // request (state polling, WebSocket) needs that full id, not the code.
      onSession(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to join game');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="home-page">
      <button
        className="ghost-button theme-toggle"
        onClick={() =>
          onSettingsChange({ ...settings, theme: settings.theme === 'dark' ? 'light' : 'dark' })
        }
        type="button"
      >
        {settings.theme === 'dark' ? '☀ Light' : '☾ Dark'}
      </button>

      <section className="home-actions">
        <div className="home-actions-inner">
          <div className="banner-side">
            <CrosswordBanner />
            <p>
              <span>Start a weekly multiplayer crossword</span>
              <span>or join with a game link.</span>
            </p>
          </div>
          <div className="action-stack">
            <label>
              <span className="field-label">Player name</span>
              <input
                maxLength={50}
                onChange={(event) => onSettingsChange({ ...settings, playerName: event.target.value })}
                placeholder="Player name"
                value={settings.playerName}
              />
            </label>
            <button
              disabled={busy || !settings.playerName.trim()}
              onClick={() => {
                setCreateError('');
                setShowCreate(true);
              }}
              type="button"
            >
              Create game
            </button>
            <div className="join-row">
              <input
                aria-label="Game ID or code"
                onChange={(event) => setJoinGameId(event.target.value)}
                placeholder="Game code (e.g. 88EDUR)"
                value={joinGameId}
              />
              <button
                disabled={busy || !joinGameId.trim() || !settings.playerName.trim()}
                onClick={joinGame}
                type="button"
              >
                Join
              </button>
            </div>
          </div>
        </div>
        {error && <p className="notice error">{error}</p>}
      </section>

      <section className="leaderboard home-leaderboard" aria-label="Global ranking">
        <h2>Global ranking</h2>
        <small>All players · last 30 days</small>
        {ranking === null && <p className="notice">Loading scores…</p>}
        {ranking !== null &&
          ranking.points_leaderboard.length === 0 &&
          ranking.time_leaderboard.length === 0 && (
          <p className="notice">No completed games in the last 30 days — be the first!</p>
        )}
        {ranking !== null && ranking.points_leaderboard.length > 0 && (
          <div className="leaderboard-list">
            <h3>Points</h3>
            {ranking.points_leaderboard.slice(0, 10).map((entry) => (
              <div className="leaderboard-row" key={entry.player_id}>
                <span className="rank-number">#{entry.rank}</span>
                <span className="rank-name">{entry.name}</span>
                <strong>{formatRankMetric(entry)}</strong>
              </div>
            ))}
          </div>
        )}
        {ranking !== null && ranking.time_leaderboard.length > 0 && (
          <div className="leaderboard-list">
            <h3>Time</h3>
            {ranking.time_leaderboard.slice(0, 10).map((entry) => (
              <div className="leaderboard-row" key={entry.player_id}>
                <span className="rank-number">#{entry.rank}</span>
                <span className="rank-name">{entry.name}</span>
                <strong>{entry.fastest_time === null ? 'No time' : formatTime(entry.fastest_time)}</strong>
              </div>
            ))}
          </div>
        )}
      </section>

      {showCreate && (
        <CreateGameDialog
          busy={busy}
          error={createError}
          onChange={onSettingsChange}
          onClose={() => setShowCreate(false)}
          onConfirm={createGame}
          settings={settings}
        />
      )}
    </main>
  );
}
